import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useRef, useState } from 'react';
import { CheckIcon, CopyIcon, GlobeIcon } from '@primer/octicons-react';
import styles from './screenshot.module.css';

/** Lock glyph — quoted from kaneai BrowserUrlBar.tsx (8×11, #6E7781). */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var LockGlyph = function LockGlyph() {
  return /*#__PURE__*/_jsx("svg", {
    width: "8",
    height: "11",
    viewBox: "0 0 8 11",
    fill: "none",
    "aria-hidden": "true",
    children: /*#__PURE__*/_jsx("path", {
      d: "M3.83301 0C5.42083 0 6.70801 1.28718 6.70801 2.875V3.83301C7.23728 3.83301 7.66699 4.26272 7.66699 4.79199V9.58301C7.66699 10.1123 7.23728 10.542 6.70801 10.542H0.958008C0.428884 10.5418 0 10.1122 0 9.58301V4.79199C0 4.26283 0.428885 3.83318 0.958008 3.83301V2.875C0.958009 1.28729 2.24534 0.0001759 3.83301 0ZM3.83301 0.958008C2.77461 0.958184 1.91602 1.81656 1.91602 2.875V3.83301H5.75V2.875C5.75 1.81645 4.89155 0.958008 3.83301 0.958008Z",
      fill: "#6E7781"
    })
  });
};
var domainOf = function domainOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch (_unused) {
    return url;
  }
};
var fullOf = function fullOf(url) {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
};

/**
 * Port of kaneai's BrowserUrlBar: 34px pill, #F6F8FA, radius 100px,
 * max-width 560px centered; click expands to the full URL with a copy
 * button. Globe glyph permanently (kaneai's own favicon fallback — the
 * public viewer makes no external requests).
 */
export function BrowserUrlBar(_ref) {
  var url = _ref.url;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    expanded = _useState2[0],
    setExpanded = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    copied = _useState4[0],
    setCopied = _useState4[1];
  var rootRef = useRef(null);
  var hasUrl = !!url;
  useEffect(function () {
    if (!expanded) return undefined;
    var onDown = function onDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setExpanded(false);
      }
    };
    var onKey = function onKey(e) {
      if (e.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return function () {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [expanded]);
  var copy = function copy(e) {
    e.stopPropagation();
    if (!url || !navigator.clipboard) return;
    var normalized = /^https?:\/\//.test(url) ? url : "https://".concat(url);
    void navigator.clipboard.writeText(normalized);
    setCopied(true);
    setTimeout(function () {
      return setCopied(false);
    }, 1500);
  };
  return /*#__PURE__*/_jsxs("div", {
    ref: rootRef,
    className: styles.urlPill,
    style: {
      maxWidth: hasUrl && expanded ? '100%' : 560,
      cursor: hasUrl ? 'pointer' : 'default'
    },
    role: hasUrl ? 'button' : undefined,
    tabIndex: hasUrl ? 0 : undefined,
    "aria-label": hasUrl ? "Page URL: ".concat(url) : undefined,
    "aria-expanded": hasUrl ? expanded : undefined,
    onClick: function onClick() {
      return hasUrl && setExpanded(true);
    },
    onKeyDown: function onKeyDown(e) {
      if (hasUrl && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        setExpanded(function (v) {
          return !v;
        });
      }
    },
    children: [/*#__PURE__*/_jsx("span", {
      className: styles.urlFavicon,
      children: /*#__PURE__*/_jsx(GlobeIcon, {
        size: 16,
        fill: "#636C76",
        "aria-hidden": "true"
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: "".concat(styles.urlText, " ").concat(hasUrl && expanded ? styles.urlTextExpanded : styles.urlTextCentered),
      children: [hasUrl && /*#__PURE__*/_jsx(LockGlyph, {}), /*#__PURE__*/_jsx("span", {
        className: styles.urlDomain,
        style: {
          color: hasUrl ? '#24292F' : '#636C76'
        },
        children: hasUrl ? expanded ? fullOf(url) : domainOf(url) : 'about:blank'
      }), hasUrl && expanded && /*#__PURE__*/_jsx("button", {
        type: "button",
        className: styles.urlCopy,
        "aria-label": "Copy page URL",
        onClick: copy,
        children: copied ? /*#__PURE__*/_jsx(CheckIcon, {
          size: 14,
          fill: "#1a7f37",
          "aria-hidden": "true"
        }) : /*#__PURE__*/_jsx(CopyIcon, {
          size: 14,
          fill: "#636C76",
          "aria-hidden": "true"
        })
      })]
    })]
  });
}